import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  selectStoreRegions,
  selectPointOfSales,
} from "../../redux/cash-in-out/cash-in-out-selectors";
import { selectCurrencies } from "../../redux/general/selector";
import {
  createRequestData,
  getDefaultLang,
  getResponseData,
} from "../../utils";
import { requestERPLYData } from "../../api/Server";
import { useAuth } from "../../context/authContext";
import { DateBox, SelectBox, TagBox, Button } from "devextreme-react";

const dateRanges = [
  "today",
  "current week",
  "current month",
  "current year",
  "yesterday",
  "last week",
  "last month",
  "last year",
  "last 15 days",
  "last 30 days",
  "last 60 days",
  "last 90 days",
];

const FilterSelector = ({ formData, handleSubmit, onValueChanged }) => {
  const [reasonCodes, setCodes] = useState([]);
  const { storeRegions, pointOfSales, currencies } = useSelector(
    createStructuredSelector({
      storeRegions: selectStoreRegions,
      pointOfSales: selectPointOfSales,
      currencies: selectCurrencies,
    })
  );

  const { user, activeLanguages, configParams, orderedWarehouses } = useAuth();

  const defaultAccountLang = getDefaultLang(
    configParams.default_language,
    activeLanguages
  );

  useEffect(() => {
    getCodes();
    // eslint-disable-next-line
  }, []);

  const getCodes = async () => {
    if (user) {
      const { clientCode, isIframe, sessionKey } = user;
      const request = {
        requests: createRequestData(100, {
          requestName: "getReasonCodes",
          recordsOnPage: 100,
          sendContentType: 1,
          purpose: "EOD_VARIANCE",
        }),
        clientCode,
      };

      if (isIframe || sessionKey) {
        request.sessionKey = sessionKey;
      }
      try {
        const { data } = await requestERPLYData(request);
        if (data.status.responseStatus !== "error") {
          setCodes(getResponseData(data.requests));
        }
      } catch (error) {}
    }
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Start Date *</span>

            <DateBox
              type="date"
              showClearButton={true}
              pickerType="calendar"
              value={formData.unixTimeStart}
              onValueChanged={(e) => onValueChanged(e, "unixTimeStart")}
              displayFormat={"EEEE, dd MMM, yyyy"}
            />
          </div>
          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">End Date *</span>
            <DateBox
              type="date"
              showClearButton={true}
              pickerType="calendar"
              value={formData.unixTimeEnd}
              onValueChanged={(e) => onValueChanged(e, "unixTimeEnd")}
              displayFormat={"EEEE, dd MMM, yyyy"}
            />
          </div>
          <div className="col-4 mt-4 mb-3">
            <SelectBox
              dataSource={dateRanges}
              showClearButton={true}
              searchEnabled={true}
              placeholder="date range selector"
              value={formData.dateRange}
              onValueChanged={(e) => onValueChanged(e, "dateRange")}
            />
          </div>

          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Currency *</span>
            <SelectBox
              showClearButton={true}
              dataSource={currencies}
              displayExpr="formattedName"
              valueExpr="currencyID"
              placeholder="select currency"
              searchEnabled={true}
              showDropDownButton={true}
              value={formData.currencyId}
              onValueChanged={(e) => onValueChanged(e, "currencyId")}
            />
          </div>
          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Registers</span>
            <TagBox
              showClearButton={true}
              dataSource={pointOfSales}
              showSelectionControls
              displayExpr="name"
              valueExpr="pointOfSaleID"
              placeholder="select register"
              searchEnabled={true}
              showDropDownButton={true}
              selectAllMode="allPages"
              maxDisplayedTags={10}
              value={formData.pointOfSaleIds}
              onValueChanged={(e) => onValueChanged(e, "pointOfSaleIds")}
            />
          </div>

          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Locations</span>
            <TagBox
              showClearButton={true}
              dataSource={orderedWarehouses}
              displayExpr={defaultAccountLang}
              showSelectionControls
              valueExpr="id"
              placeholder="select location"
              searchEnabled={true}
              showDropDownButton={true}
              selectAllMode="allPages"
              maxDisplayedTags={10}
              value={formData.warehouseIds}
              onValueChanged={(e) => onValueChanged(e, "warehouseIds")}
            />
          </div>

          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Store Regions</span>
            <TagBox
              showClearButton={true}
              dataSource={storeRegions}
              showSelectionControls
              displayExpr="name"
              valueExpr="storeRegionID"
              placeholder="select store region"
              searchEnabled={true}
              showDropDownButton={true}
              selectAllMode="allPages"
              maxDisplayedTags={10}
              value={formData.warehouseRegionIds}
              onValueChanged={(e) => onValueChanged(e, "warehouseRegionIds")}
            />
          </div>

          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Over or Short</span>
            <SelectBox
              dataSource={[
                { name: "Over", value: "OVER" },
                { name: "Short", value: "SHORT" },
              ]}
              showClearButton={true}
              searchEnabled={true}
              displayExpr="name"
              valueExpr="value"
              placeholder="select over or short"
              value={formData.overOrShort}
              onValueChanged={(e) => onValueChanged(e, "overOrShort")}
            />
          </div>
          <div className="col-4 mb-3">
            <span className="sub-title text-capitalize">Reason Codes</span>
            <TagBox
              showClearButton={true}
              dataSource={reasonCodes}
              showSelectionControls
              displayExpr="name"
              valueExpr="reasonID"
              placeholder="select reason code"
              searchEnabled={true}
              showDropDownButton={true}
              selectAllMode="allPages"
              maxDisplayedTags={10}
              value={formData.varianceReasonCodeIds}
              onValueChanged={(e) => onValueChanged(e, "varianceReasonCodeIds")}
            />
          </div>
        </div>
        <Button
          useSubmitBehavior={true}
          className="mt-3"
          stylingMode="contained"
          type="default"
          text="Get report"
          elementAttr={{ style: "background-color: #0737a0" }}
        />
      </form>
    </div>
  );
};

export default FilterSelector;
